import React, { useReducer, useState } from 'react'
import { Line } from 'react-chartjs-2'

import { Performance } from 'generated/types'
import { PERFORMANCE_CHART } from 'utils/testing/testIDs'

import {
  FormattedDataType,
  formatChartValues,
  getChartData,
  getClosestDate,
  getMouseEventValue,
  getOptions,
  getTouchEventValue,
} from './helpers'
import { ActionType, initialState, reducer } from './reducer'
import { ChartWrapper, HandlerWrapper } from './styled-components'
import { TooltipOverlay } from './TooltipOverlay'

export const Chart = ({
  accountValues,
  onSetAccountValue,
}: {
  onSetAccountValue: React.Dispatch<React.SetStateAction<number>>
} & Pick<Performance, 'accountValues'>): React.ReactElement => {
  const [
    {
      closestIndex,
      elWidth,
      isDragging,
      tension,
      isTooltipActive,
      tooltipOffsetX,
    },
    dispatch,
  ] = useReducer(reducer, initialState)
  const [{ dates, amounts }] = useState<FormattedDataType>(
    formatChartValues(accountValues),
  )

  const handleMouseStartMove = (evt: React.MouseEvent) => {
    if (!isDragging && evt.type === 'mousedown') {
      dispatch({ type: ActionType.DRAG_START })
    } else if (!isDragging && evt.type === 'mousemove') {
      return
    }

    const { elWidth, offsetX } = getMouseEventValue(evt)
    const { closestIndex, tooltipOffsetX } = getClosestDate({
      elWidth,
      offsetX,
      totalDates: amounts.length,
    })

    dispatch({
      type: ActionType.SET_TOOLTIP,
      payload: { closestIndex, elWidth, tooltipOffsetX },
    })
    onSetAccountValue(amounts?.[closestIndex] || 0)
  }

  const handleTouchStartMove = (evt: React.TouchEvent) => {
    const { elWidth, offsetX } = getTouchEventValue(evt)

    if (offsetX < elWidth && offsetX > 0) {
      const { closestIndex, tooltipOffsetX } = getClosestDate({
        elWidth,
        offsetX,
        totalDates: amounts.length,
      })

      dispatch({
        type: ActionType.SET_TOOLTIP,
        payload: { closestIndex, elWidth, tooltipOffsetX },
      })
      onSetAccountValue(amounts?.[closestIndex] || 0)
    }
  }

  const handleEventEnd = () => {
    dispatch({
      type: ActionType.REMOVE_TOOLTIP,
    })
    onSetAccountValue(amounts?.[amounts.length - 1] || 0)
  }

  return (
    <HandlerWrapper
      onMouseDown={handleMouseStartMove}
      onMouseMove={handleMouseStartMove}
      onMouseUp={handleEventEnd}
      onTouchStart={handleTouchStartMove}
      onTouchMove={handleTouchStartMove}
      onTouchEnd={handleEventEnd}
      onMouseLeave={handleEventEnd}
    >
      <ChartWrapper data-testid={PERFORMANCE_CHART}>
        <TooltipOverlay
          accountValue={amounts[closestIndex]}
          date={dates[closestIndex]}
          elWidth={elWidth}
          isActive={isTooltipActive}
          offsetX={tooltipOffsetX}
        />
        {/* @ts-ignore */}
        <Line
          data={() => getChartData({ dates, amounts })}
          options={getOptions(tension)}
        />
      </ChartWrapper>
    </HandlerWrapper>
  )
}
